import React, { useEffect, useRef } from 'react';
import XiLogo from '../ui/XiLogo';
import useTheme from '../../hooks/useTheme';
import useTranslation from '../../hooks/useTranslation';
import { LuSun, LuMoon } from 'react-icons/lu';

/**
 * Xi application shell.
 *
 * One shell for every role workspace. Navigation is supplied by the page so
 * the shell never invents routes or business state. On desktop it renders a
 * persistent rail, on tablet an icon rail, and on mobile a bottom bar.
 */
export default function AppShell({
  role = 'workspace',
  workspace = 'Workspace',
  title,
  subtitle,
  nav = [],
  activeKey,
  onNavigate,
  actions,
  footer,
  children,
  className = '',
}) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const mainRef = useRef(null);

  useEffect(() => {
    if (mainRef.current) mainRef.current.setAttribute('tabindex', '-1');
  }, []);

  const activate = (item) => {
    if (!item) return;
    if (typeof item.onClick === 'function') {
      item.onClick();
      return;
    }
    if (item.href) {
      window.location.href = item.href;
      return;
    }
    if (onNavigate) onNavigate(item.key);
  };

  const renderItem = (item, mobile = false) => {
    const active = activeKey === item.key;
    return (
      <button
        key={item.key}
        type="button"
        className={
          mobile
            ? `xi-bottomnav__item${active ? ' is-active' : ''}`
            : `xi-nav-item${active ? ' is-active' : ''}`
        }
        aria-current={active ? 'page' : undefined}
        aria-label={item.label}
        onClick={() => activate(item)}
        title={item.label}
      >
        {item.icon}
        {mobile ? (
          <span className="xi-bottomnav__label">{item.shortLabel || item.label}</span>
        ) : (
          <span className="xi-nav-item__label">{item.label}</span>
        )}
        {!mobile && item.badge ? <span className="xi-nav-item__badge">{item.badge}</span> : null}
      </button>
    );
  };

  return (
    <div className={`xi-shell ${className}`.trim()} data-role={role}>
      <a href="#xi-main" className="xi-sr-only">
        {t('Skip to content')}
      </a>

      <aside className="xi-shell__rail" aria-label={`${workspace} ${t('navigation')}`}>
        <div className="xi-shell__brand">
          <XiLogo size="sm" wordmark={false} label="" />
        </div>
        <div className="xi-shell__workspace">{workspace}</div>
        <nav className="xi-shell__nav" aria-label={t('Primary')}>
          {nav.map((item) => renderItem(item))}
        </nav>
        <div className="xi-shell__foot">
          <button
            type="button"
            className="xi-nav-item"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('Switch to light theme') : t('Switch to dark theme')}
          >
            {theme === 'dark' ? <LuSun /> : <LuMoon />}
            <span className="xi-nav-item__label">{t('Appearance')}</span>
          </button>
          {footer}
        </div>
      </aside>

      <div className="xi-shell__body">
        <header className="xi-topbar">
          <div className="xi-topbar__title">
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {actions ? <div className="xi-topbar__actions">{actions}</div> : null}
        </header>
        <main id="xi-main" className="xi-main" ref={mainRef}>
          {children}
        </main>
      </div>

      {nav.length > 0 ? (
        <nav className="xi-bottomnav" aria-label={`${workspace} ${t('navigation')}`}>
          {nav.slice(0, 5).map((item) => renderItem(item, true))}
        </nav>
      ) : null}
    </div>
  );
}
